/**
 * The two decisions GrowArea makes while someone types, as plain numbers:
 * how tall the field is, and how far to scroll so the cursor's line stays
 * in sight. No DOM here, so they can be checked on their own
 * (scripts/verify/notes-caret.ts).
 */

/** What a field measures: the off-screen copy, and the field itself. */
export interface FitIn {
  /** The copy's height for the text, and for the field's rows (its minimum), borders included. */
  twinNeed: number;
  twinMin: number;
  /** The real field, as it is laid out now. */
  scrollHeight: number;
  clientHeight: number;
  offsetHeight: number;
}

/**
 * The height to give the field, in px: what the copy says, but never less
 * than the field itself says it needs. The copy can come out a line short
 * on iOS (a new line just typed, the text inset); with overflow hidden the
 * line being typed was then cut off and the cursor went out of sight.
 */
export function fitHeight(i: FitIn): number {
  const own = i.scrollHeight > i.clientHeight + 1 ? i.offsetHeight + i.scrollHeight - i.clientHeight : 0;
  return Math.max(i.twinNeed, i.twinMin, own);
}

/** Where things are on screen, in px from the top of the viewport. */
export interface Geometry {
  /** What the keyboard, the sheet's frame and a pinned bar leave visible. */
  visTop: number;
  visBottom: number;
  lineH: number;
  /** The bottom of the cursor's line. */
  caret: number;
  /** The field's top and its last line of text. */
  boxTop: number;
  lastLine: number;
  /** The label right above the field, if it sits close to it. */
  labelTop: number | null;
  /** A rating or notes sheet: two lines of room under the cursor. */
  roomy: boolean;
}

/**
 * Why it is being placed: the field was just focused, the text changed, or
 * the visible area changed (the keyboard, its suggestion bar, the app).
 */
export type Reason = 'focus' | 'typing' | 'resize';

/**
 * How far to scroll (positive: down); 0 when it is fine where it is.
 *
 * Only a fresh focus places the whole field and its label. A change of the
 * visible area while typing — on iOS the suggestion bar comes and goes as
 * words are typed — only keeps the cursor's line in sight: placing the
 * label again scrolled the sheet back up under the coach's fingers.
 */
export function caretDelta(g: Geometry, reason: Reason): number {
  const focusing = reason === 'focus';
  const below = g.roomy ? 2 * g.lineH : 16;
  const above = g.roomy ? 12 : 16;
  // The top of what should stay visible: the label when focusing,
  // otherwise the cursor's own line.
  const top = focusing && g.roomy ? (g.labelTop ?? g.boxTop) : g.caret - g.lineH;
  // On focus the whole field gets the room when it fits with its label.
  const fits = g.lastLine - top + below + above <= g.visBottom - g.visTop;
  const anchor = focusing && g.roomy && fits ? Math.max(g.caret, g.lastLine) : g.caret;

  const atLeast = anchor + below - g.visBottom;  // scroll down at least this much
  const atMost = top - above - g.visTop;         // and at most this much
  let delta = 0;
  if (atLeast > 0) delta = atLeast;              // the cursor's room comes first
  else if (atMost < 0) delta = Math.max(atMost, atLeast);
  return Math.abs(delta) < 1 ? 0 : delta;
}
